import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
  Text,
  VStack,
  Textarea,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  useColorModeValue,
} from '@chakra-ui/react'
import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { TEXT_CONSTRAINTS } from '@shared/inputConstraints'
import { validateTextWithConstraint } from '@shared/textConstraintHelpers'

interface StatusConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  action: string
  merchantName: string
  onConfirm: (action: string, reason: string) => Promise<void>
  isLoading: boolean
}

export function StatusConfirmDialog({
  isOpen,
  onClose,
  action,
  merchantName,
  onConfirm,
  isLoading
}: StatusConfirmDialogProps) {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')
  const [reasonError, setReasonError] = useState<string | null>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const bgColor = useColorModeValue('white', 'gray.800')
  const reasonConstraint = TEXT_CONSTRAINTS['merchant.statusReason']

  const getActionConfig = (action: string) => {
    const configs = {
      activate: {
        title: t('merchantActions.confirmActivation'),
        message: t('merchantActions.messages.activation'),
        colorScheme: 'green',
        isDangerous: false,
        placeholder: t('merchantActions.placeholders.activation')
      },
      suspend: {
        title: t('merchantActions.confirmSuspension'),
        message: t('merchantActions.messages.suspension'),
        colorScheme: 'orange',
        isDangerous: false,
        placeholder: t('merchantActions.placeholders.suspension')
      },
      terminate: {
        title: t('merchantActions.confirmTermination'),
        message: t('merchantActions.messages.termination'),
        colorScheme: 'red',
        isDangerous: true,
        placeholder: t('merchantActions.placeholders.termination')
      }
    }
    return configs[action as keyof typeof configs] || {
      title: t('merchantActions.confirmOperation'),
      message: t('merchantActions.messages.default'),
      colorScheme: 'blue',
      isDangerous: false,
      placeholder: t('merchantActions.operationReason')
    }
  }

  const config = getActionConfig(action)
  const isReasonRequired = ['terminate'].includes(action)

  const handleConfirm = async () => {
    try {
      const validation = validateTextWithConstraint(reason, reasonConstraint, {
        requiredOverride: isReasonRequired,
      })

      if (!validation.valid) {
        const message = validation.error
          ? t('merchantActions.reasonValidation', { message: validation.error })
          : isReasonRequired
            ? t('merchantActions.reasonRequired')
            : null
        setReasonError(message)
        return
      }

      const sanitizedReason = validation.value ?? ''

      await onConfirm(action, sanitizedReason)
      setReason('')
      setReasonError(null)
    } catch (error) {
      // Error handling is done in parent component
    }
  }

  const handleClose = () => {
    setReason('')
    setReasonError(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={handleClose}
      size="md"
    >
      <AlertDialogOverlay>
        <AlertDialogContent bg={bgColor} mx={4}>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {config.title}
          </AlertDialogHeader>

          <AlertDialogBody>
            <VStack align="stretch" spacing={4}>
              <Text>
                {t('merchantActions.merchant')}：<Text as="span" fontWeight="semibold">{merchantName}</Text>
              </Text>
              
              <Text 
                color={config.isDangerous ? 'red.600' : 'gray.600'}
                fontSize="sm"
              >
                {config.message}
              </Text>

              <FormControl isRequired={isReasonRequired} isInvalid={!!reasonError}>
                <FormLabel fontSize="sm">{t('merchantActions.operationReason')}</FormLabel>
                <Textarea
                  value={reason}
                  onChange={(e) => {
                    const nextValue = reasonConstraint.maxLength
                      ? e.target.value.slice(0, reasonConstraint.maxLength)
                      : e.target.value
                    setReason(nextValue)
                    if (reasonError) {
                      setReasonError(null)
                    }
                  }}
                  placeholder={config.placeholder}
                  rows={3}
                  resize="none"
                  maxLength={reasonConstraint.maxLength ?? undefined}
                />
                {isReasonRequired && (
                  <FormHelperText color="red.500">
                    {t('merchantActions.reasonRequired')}
                  </FormHelperText>
                )}
                {reasonError && (
                  <FormErrorMessage>{reasonError}</FormErrorMessage>
                )}
              </FormControl>
            </VStack>
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button
              ref={cancelRef}
              onClick={handleClose}
              size="sm"
              disabled={isLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              colorScheme={config.colorScheme}
              onClick={handleConfirm}
              ml={3}
              size="sm"
              isLoading={isLoading}
              loadingText={t('common.processing')}
              isDisabled={isReasonRequired && !reason.trim()}
            >
              {config.isDangerous ? t('common.confirm') : t('common.submit')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}
